import { cn } from '@/lib/utils';
import {
  ArrowDown,
  ArrowUp,
  Download,
  Globe,
  Loader2,
  MessageSquare,
  Search,
  Smartphone
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import {
  CustomerRow,
  ThreadSummary,
  UsageOverview as Overview,
  UsageUserDetail,
  UsageUserRow,
  UserStatus,
  apiAdminThreads,
  apiAdminUsageOverview,
  apiAdminUsageUser,
  formatDateTime,
  loginMethodLabel,
  timeAgo
} from './api';
import { OpenThreadRef } from './ConversationBrowser';

// Usage across every company in the admin's scope: who is here, who stopped
// and who never came. Everything is counted by the server
// (/api/admin/usage/overview); a question is counted the way the
// Conversations tab counts it, so the two pages always agree.

const STATUS: Record<UserStatus, { label: string; cls: string; help: string }> = {
  active: {
    label: 'Active',
    cls: 'border-emerald-300 text-emerald-700 dark:border-emerald-800 dark:text-emerald-300',
    help: 'Seen in the last 7 days'
  },
  idle: {
    label: 'Idle',
    cls: 'border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-300',
    help: 'Last seen 8 to 30 days ago'
  },
  dormant: {
    label: 'Dormant',
    cls: 'border-red-300 text-red-700 dark:border-red-800 dark:text-red-300',
    help: 'Not seen for over 30 days'
  },
  never: {
    label: 'Never used',
    cls: 'border-muted-foreground/40 text-muted-foreground',
    help: 'No sign-in, visit or question recorded'
  },
  disabled: {
    label: 'Disabled',
    cls: 'border-muted-foreground/40 text-muted-foreground',
    help: 'Switched off in Users'
  },
  expired: {
    label: 'Expired',
    cls: 'border-muted-foreground/40 text-muted-foreground',
    help: 'Past the last day of access'
  }
};

type SortKey =
  | 'email'
  | 'customer_name'
  | 'last_seen_at'
  | 'last_login_at'
  | 'questions'
  | 'questions_total'
  | 'active_days'
  | 'quota'
  | 'cost_usd';

const STATUS_FILTERS: { id: 'all' | UserStatus; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'idle', label: 'Idle' },
  { id: 'dormant', label: 'Dormant' },
  { id: 'never', label: 'Never used' },
  { id: 'disabled', label: 'Disabled' },
  { id: 'expired', label: 'Expired' }
];

function ClientIcon({ client }: { client: string | null }) {
  if (client === 'mobile')
    return <Smartphone className="size-3.5 inline" aria-label="mobile app" />;
  if (client === 'web')
    return <Globe className="size-3.5 inline" aria-label="web" />;
  return null;
}

function quotaOf(u: UsageUserRow): number {
  return u.base_message_limit
    ? (u.monthly_message_count ?? 0) / u.base_message_limit
    : 0;
}

export default function UsageOverview({
  customers,
  sees_cost,
  onOpenThread,
  onOpenCustomer,
  onError
}: {
  customers: CustomerRow[];
  sees_cost: boolean;
  onOpenThread: (t: OpenThreadRef) => void;
  onOpenCustomer: (pk: number) => void;
  onError?: (message: string) => void;
}) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [company, setCompany] = useState<number | ''>('');
  const [status, setStatus] = useState<'all' | UserStatus>('all');
  const [admins, setAdmins] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({
    key: 'last_seen_at',
    desc: true
  });
  const [detail, setDetail] = useState<UsageUserRow | null>(null);

  useEffect(() => {
    setLoading(true);
    apiAdminUsageOverview(days)
      .then(setData)
      .catch((e) => onError?.(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    const list = data.items.filter(
      (u) =>
        (admins || u.role === 'USER') &&
        (company === '' || u.customer_pk === company) &&
        (status === 'all' || u.status === status) &&
        (!needle ||
          u.email.toLowerCase().includes(needle) ||
          u.customer_name.toLowerCase().includes(needle))
    );
    const val = (u: UsageUserRow): number | string => {
      switch (sort.key) {
        case 'email':
          return u.email.toLowerCase();
        case 'customer_name':
          return u.customer_name.toLowerCase();
        case 'last_seen_at':
          return u.last_seen_at ? Date.parse(u.last_seen_at) : 0;
        case 'last_login_at':
          return u.last_login_at ? Date.parse(u.last_login_at) : 0;
        case 'quota':
          return quotaOf(u);
        default:
          return (u[sort.key] as number | undefined) ?? 0;
      }
    };
    return [...list].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      const c = x < y ? -1 : x > y ? 1 : 0;
      return sort.desc ? -c : c;
    });
  }, [data, q, company, status, admins, sort]);

  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    (data?.items ?? [])
      .filter((u) => admins || u.role === 'USER')
      .filter((u) => company === '' || u.customer_pk === company)
      .forEach((u) => (m[u.status] = (m[u.status] ?? 0) + 1));
    return m;
  }, [data, admins, company]);

  const exportCsv = () => {
    const head = [
      'company',
      'email',
      'status',
      'last_seen',
      'last_sign_in',
      'sign_in_method',
      `questions_${days}d`,
      'questions_all_time',
      `conversations_${days}d`,
      'active_days',
      'messages_this_month',
      'monthly_limit',
      ...(sees_cost ? [`cost_usd_${days}d`] : [])
    ];
    const esc = (v: unknown) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = rows.map((u) =>
      [
        u.customer_name,
        u.email,
        u.status,
        u.last_seen_at ?? '',
        u.last_login_at ?? '',
        u.last_login_method ?? '',
        u.questions,
        u.questions_total,
        u.conversations,
        u.active_days,
        u.monthly_message_count ?? 0,
        u.base_message_limit ?? '',
        ...(sees_cost ? [u.cost_usd ?? 0] : [])
      ]
        .map(esc)
        .join(',')
    );
    const blob = new Blob(['﻿' + [head.join(','), ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8'
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `signals-usage-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const th = (key: SortKey, label: string, cls = '') => (
    <th className={cn('py-2 pr-3 font-medium whitespace-nowrap', cls)}>
      <button
        className="inline-flex items-center gap-1 hover:text-foreground"
        onClick={() =>
          setSort((s) =>
            s.key === key
              ? { key, desc: !s.desc }
              : { key, desc: key !== 'email' && key !== 'customer_name' }
          )
        }
      >
        {label}
        {sort.key === key &&
          (sort.desc ? (
            <ArrowDown className="size-3" />
          ) : (
            <ArrowUp className="size-3" />
          ))}
      </button>
    </th>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">
          Every user in {customers.length === 1 ? 'the company' : 'every company'}{' '}
          you manage: when they were last here, how they signed in and what
          they asked.
        </p>
        <div className="flex items-center gap-2">
          {loading && (
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          )}
          <div className="flex rounded-md border overflow-hidden">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={cn(
                  'px-3 h-8 text-xs border-r last:border-r-0',
                  days === d
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-muted-foreground'
                )}
              >
                {d} days
              </button>
            ))}
          </div>
        </div>
      </div>

      {data && (
        <>
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
            <Tile
              label="Active today"
              value={data.totals.active_1d}
              sub={`of ${data.totals.users} users`}
            />
            <Tile
              label="Active 7 days"
              value={data.totals.active_7d}
              sub={pct(data.totals.active_7d, data.totals.users)}
            />
            <Tile
              label="Active 30 days"
              value={data.totals.active_30d}
              sub={pct(data.totals.active_30d, data.totals.users)}
            />
            <Tile
              label={`Questions, ${days} days`}
              value={data.totals.questions}
              sub={`${data.totals.conversations} conversations`}
            />
            <Tile
              label="Never used"
              value={data.totals.never}
              sub={`${data.totals.dormant} dormant`}
              tone={data.totals.never + data.totals.dormant > 0 ? 'warn' : undefined}
              onClick={() => setStatus('never')}
            />
            {data.totals.cost_usd !== undefined ? (
              <Tile
                label={`Our cost, ${days} days`}
                value={`$${data.totals.cost_usd.toFixed(2)}`}
                sub={
                  data.totals.questions
                    ? `$${(data.totals.cost_usd / data.totals.questions).toFixed(3)} / question`
                    : undefined
                }
              />
            ) : (
              <Tile label="Companies" value={data.totals.companies} />
            )}
          </div>

          <Card className="p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <div className="font-medium text-sm">Day by day</div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="inline-block size-2.5 rounded-sm bg-primary/70" />{' '}
                  questions
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-0.5 bg-amber-500" />{' '}
                  active users
                </span>
              </div>
            </div>
            <DailyChart series={data.series} />
            <p className="text-[11px] text-muted-foreground mt-2">
              {data.tracking_since
                ? `Visits and sign-ins are recorded since ${data.tracking_since}; before that a user counts as active on the days they asked a question.`
                : 'Visits and sign-ins are recorded from today; until then a user counts as active on the days they asked a question.'}
            </p>
          </Card>

          <Card className="p-4 flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="size-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Find a user or company"
                  className="h-8 w-56 pl-8"
                />
              </div>
              <select
                className="h-8 rounded-md border bg-background px-2 text-sm"
                value={company}
                onChange={(e) =>
                  setCompany(e.target.value ? Number(e.target.value) : '')
                }
              >
                <option value="">All companies</option>
                {customers.map((c) => (
                  <option key={c.pk} value={c.pk}>
                    {c.name}
                  </option>
                ))}
              </select>
              <div className="flex flex-wrap gap-1">
                {STATUS_FILTERS.map((s) => {
                  const n =
                    s.id === 'all'
                      ? Object.values(counts).reduce((a, b) => a + b, 0)
                      : counts[s.id] ?? 0;
                  if (s.id !== 'all' && n === 0 && status !== s.id) return null;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setStatus(s.id)}
                      className={cn(
                        'text-xs rounded-full border px-2.5 py-0.5',
                        status === s.id
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'text-muted-foreground hover:bg-muted'
                      )}
                    >
                      {s.label} {n}
                    </button>
                  );
                })}
              </div>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
                <input
                  type="checkbox"
                  checked={admins}
                  onChange={(e) => setAdmins(e.target.checked)}
                />
                admin accounts
              </label>
              <Button variant="outline" size="sm" className="h-8" onClick={exportCsv}>
                <Download className="size-3.5 mr-1" /> CSV
              </Button>
            </div>

            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    {th('email', 'User')}
                    <th className="py-2 pr-3 font-medium">Status</th>
                    {th('last_seen_at', 'Last seen')}
                    {th('last_login_at', 'Last sign-in')}
                    {th('questions', `Questions ${days}d`, 'text-right')}
                    {th('questions_total', 'All time', 'text-right')}
                    {th('active_days', 'Active days', 'text-right')}
                    {th('quota', 'This month')}
                    {sees_cost && th('cost_usd', 'Cost', 'text-right')}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b last:border-b-0 hover:bg-muted/40 cursor-pointer"
                      onClick={() => setDetail(u)}
                    >
                      <td className="py-2 pr-3">
                        <div className="font-medium">{u.email}</div>
                        <div className="text-xs text-muted-foreground">
                          {u.customer_name}
                          {u.role !== 'USER' && ` · ${u.role}`}
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <Badge
                          variant="outline"
                          className={cn('text-[11px] whitespace-nowrap', STATUS[u.status].cls)}
                          title={STATUS[u.status].help}
                        >
                          {STATUS[u.status].label}
                        </Badge>
                      </td>
                      <td
                        className="py-2 pr-3 whitespace-nowrap"
                        title={formatDateTime(u.last_seen_at)}
                      >
                        <span className="flex items-center gap-1.5">
                          {timeAgo(u.last_seen_at)}
                          <span className="text-muted-foreground">
                            <ClientIcon client={u.last_client} />
                          </span>
                        </span>
                      </td>
                      <td
                        className="py-2 pr-3 whitespace-nowrap"
                        title={formatDateTime(u.last_login_at)}
                      >
                        {u.last_login_at ? (
                          <>
                            {timeAgo(u.last_login_at)}
                            <div className="text-xs text-muted-foreground">
                              {loginMethodLabel(u.last_login_method)}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {u.questions || <span className="text-muted-foreground">0</span>}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                        {u.questions_total}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                        {u.active_days || '—'}
                      </td>
                      <td className="py-2 pr-3 min-w-[110px]">
                        {u.role === 'USER' && u.base_message_limit ? (
                          <div title="Messages this month / monthly limit">
                            <div className="text-xs tabular-nums">
                              {u.monthly_message_count ?? 0} / {u.base_message_limit}
                            </div>
                            <div className="h-1 rounded bg-muted mt-1 overflow-hidden">
                              <div
                                className={cn(
                                  'h-full',
                                  quotaOf(u) >= 0.9 ? 'bg-red-500' : 'bg-primary/70'
                                )}
                                style={{ width: `${Math.min(100, quotaOf(u) * 100)}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      {sees_cost && (
                        <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                          {u.cost_usd ? `$${u.cost_usd.toFixed(2)}` : '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="py-6 text-center text-muted-foreground">
                        No user matches.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
      {!data && loading && (
        <Card className="p-6 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="size-4 animate-spin" /> Counting…
        </Card>
      )}

      <UserDetailDialog
        user={detail}
        onClose={() => setDetail(null)}
        onOpenThread={(t) => {
          setDetail(null);
          onOpenThread(t);
        }}
        onOpenCustomer={(pk) => {
          setDetail(null);
          onOpenCustomer(pk);
        }}
      />
    </div>
  );
}

function pct(n: number, of: number): string | undefined {
  return of ? `${Math.round((n / of) * 100)}% of users` : undefined;
}

function Tile({
  label,
  value,
  sub,
  tone,
  onClick
}: {
  label: string;
  value: number | string;
  sub?: string;
  tone?: 'warn';
  onClick?: () => void;
}) {
  return (
    <Card
      className={cn('p-3', onClick && 'cursor-pointer hover:bg-muted/40')}
      onClick={onClick}
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={cn(
          'text-2xl font-semibold tabular-nums mt-0.5',
          tone === 'warn' && 'text-amber-600 dark:text-amber-400'
        )}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

// Bars = questions, line = active users. Plain SVG: a few dozen points, and
// the page should not pull a chart library for it.
function DailyChart({
  series
}: {
  series: { day: string; questions: number; active_users: number }[];
}) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720;
  const H = 140;
  const pad = { l: 28, r: 28, t: 8, b: 20 };
  const n = series.length || 1;
  const maxQ = Math.max(1, ...series.map((d) => d.questions));
  const maxU = Math.max(1, ...series.map((d) => d.active_users));
  const bw = (W - pad.l - pad.r) / n;
  const y = (v: number, max: number) => pad.t + (H - pad.t - pad.b) * (1 - v / max);
  const line = series
    .map((d, i) => `${i ? 'L' : 'M'}${pad.l + bw * i + bw / 2},${y(d.active_users, maxU)}`)
    .join(' ');
  const tick = Math.ceil(n / 8);
  const h = hover !== null ? series[hover] : null;
  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-36"
        preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}
      >
        <text x={2} y={pad.t + 8} className="fill-muted-foreground text-[9px]">
          {maxQ}
        </text>
        <text x={W - 2} y={pad.t + 8} textAnchor="end" className="fill-amber-600 text-[9px]">
          {maxU}
        </text>
        {series.map((d, i) => (
          <g key={d.day} onMouseEnter={() => setHover(i)}>
            <rect
              x={pad.l + bw * i}
              y={pad.t}
              width={bw}
              height={H - pad.t - pad.b}
              className="fill-transparent"
            />
            <rect
              x={pad.l + bw * i + bw * 0.15}
              y={y(d.questions, maxQ)}
              width={bw * 0.7}
              height={H - pad.b - y(d.questions, maxQ)}
              rx={1.5}
              className={cn('fill-primary/60', hover === i && 'fill-primary')}
            />
            {i % tick === 0 && (
              <text
                x={pad.l + bw * i + bw / 2}
                y={H - 6}
                textAnchor="middle"
                className="fill-muted-foreground text-[9px]"
              >
                {d.day.slice(5)}
              </text>
            )}
          </g>
        ))}
        <path d={line} fill="none" strokeWidth={1.5} className="stroke-amber-500" />
      </svg>
      {h && (
        <div className="absolute top-0 right-0 rounded-md border bg-background/95 px-2 py-1 text-xs shadow-sm pointer-events-none">
          <div className="font-medium">{h.day}</div>
          <div>{h.questions} questions</div>
          <div>{h.active_users} active users</div>
        </div>
      )}
    </div>
  );
}

function UserDetailDialog({
  user,
  onClose,
  onOpenThread,
  onOpenCustomer
}: {
  user: UsageUserRow | null;
  onClose: () => void;
  onOpenThread: (t: OpenThreadRef) => void;
  onOpenCustomer: (pk: number) => void;
}) {
  const [detail, setDetail] = useState<UsageUserDetail | null>(null);
  const [recent, setRecent] = useState<ThreadSummary[] | null>(null);

  useEffect(() => {
    setDetail(null);
    setRecent(null);
    if (!user) return;
    apiAdminUsageUser(user.pk, 90)
      .then(setDetail)
      .catch(() => undefined);
    apiAdminThreads({ customerPk: user.customer_pk, userId: user.id, limit: 8 })
      .then((r) => setRecent(r.items))
      .catch(() => setRecent([]));
  }, [user]);

  const maxQ = Math.max(1, ...(detail?.series ?? []).map((d) => d.questions));

  return (
    <Dialog open={user !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {user?.email}
            {user && (
              <Badge variant="outline" className={cn('text-[11px]', STATUS[user.status].cls)}>
                {STATUS[user.status].label}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        {user && (
          <div className="flex flex-col gap-4 text-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Fact label="Company">
                <button
                  className="text-primary hover:underline"
                  onClick={() => onOpenCustomer(user.customer_pk)}
                >
                  {user.customer_name}
                </button>
              </Fact>
              <Fact label="Last seen">
                <span title={formatDateTime(user.last_seen_at)}>
                  {timeAgo(user.last_seen_at)}
                </span>
              </Fact>
              <Fact label="Questions">
                {user.questions_total} all time
              </Fact>
              <Fact label="Avg answer">
                {user.avg_answer_ms ? `${(user.avg_answer_ms / 1000).toFixed(1)} s` : '—'}
              </Fact>
            </div>

            <div>
              <div className="text-xs text-muted-foreground mb-1">
                Last 90 days · bars = questions, dots = visits (web / mobile)
              </div>
              {detail ? (
                <div className="flex items-end gap-px h-16 border-b">
                  {detail.series.map((d) => {
                    const web = d.visits.web ?? 0;
                    const mob = d.visits.mobile ?? 0;
                    return (
                      <div
                        key={d.day}
                        className="flex-1 flex flex-col justify-end items-center h-full"
                        title={`${d.day}: ${d.questions} questions${web ? `, web` : ''}${mob ? `, mobile` : ''}`}
                      >
                        {(web > 0 || mob > 0) && (
                          <span
                            className={cn(
                              'size-1 rounded-full mb-0.5',
                              mob ? 'bg-amber-500' : 'bg-sky-500'
                            )}
                          />
                        )}
                        <div
                          className="w-full bg-primary/60 rounded-t-sm"
                          style={{ height: `${(d.questions / maxQ) * 80}%` }}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
            </div>

            <div>
              <div className="font-medium mb-1 flex items-center gap-1.5">
                <MessageSquare className="size-4" /> Latest conversations
              </div>
              {recent === null && (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
              {recent?.length === 0 && (
                <div className="text-xs text-muted-foreground">
                  No conversations yet.
                </div>
              )}
              <div className="flex flex-col divide-y border rounded-md">
                {(recent ?? []).map((t) => (
                  <button
                    key={t.id}
                    className="text-left px-3 py-2 hover:bg-muted/40"
                    onClick={() => onOpenThread({ id: t.id, customer_pk: t.customer_pk })}
                  >
                    <div className="flex justify-between gap-2">
                      <span className="truncate" dir="auto">
                        {t.name || '(untitled conversation)'}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {timeAgo(t.last_activity)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t.question_count} {t.question_count === 1 ? 'question' : 'questions'}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="font-medium mb-1">Sign-ins</div>
              {detail && detail.logins.length === 0 && (
                <div className="text-xs text-muted-foreground">
                  None recorded yet. Sign-ins are recorded from 23 Oct 2026; a
                  user who stays signed in shows up under visits instead.
                </div>
              )}
              <div className="flex flex-col gap-1">
                {(detail?.logins ?? []).map((l, i) => (
                  <div key={i} className="flex justify-between gap-3 text-xs">
                    <span className="flex items-center gap-1.5">
                      <ClientIcon client={l.client} />
                      {loginMethodLabel(l.method)}
                      {l.ip && <span className="text-muted-foreground">· {l.ip}</span>}
                    </span>
                    <span className="text-muted-foreground">
                      {formatDateTime(l.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
